import { create } from "zustand";
import type {
  EmailAttachment,
  EmailBusinessInfo,
  EmailIdentifier,
  Template,
} from "@components/Tax Pro Only/Modals/EmailModal/types.ts";
import {
  createEmailTemplate,
  deleteEmailTemplate,
  generateEmailOpenAi,
  getEmailTemplates,
  sendEmailData,
  updateEmailTemplate,
} from "@api/email.ts";
import { notifications } from "@mantine/notifications";
import { createName } from "@components/Tax Pro Only/Modals/EmailModal/utils.ts";
import { getSearchResults } from "@api/search.ts";
import {
  NEW_TEMPLATE_ID,
  NEW_TEMPLATE_NAME,
} from "@components/Tax Pro Only/Modals/EmailModal/constants.ts";
import { attempt, SmartwizError } from "@utils/errors.ts";
import { processEmailBody } from "@/utils/emailUtils.ts";
import { useStatusStore } from "@/utils/stores/useStatusStore.ts"; // import the status store

type EmailPanelSource = "clientList" | "clientProfile";

const DEFAULT_EMAIL_SOURCE = "clientProfile";

interface EmailState {
  // Source component calling email.
  source: EmailPanelSource;
  setSource: (source: EmailPanelSource) => Promise<void>;

  // Email Form
  recipients: EmailIdentifier[];
  globalRecipients: EmailIdentifier[];
  setRecipients: (recipients: EmailIdentifier[]) => void;

  // BusinessInfo
  businessInfo?: EmailBusinessInfo;
  setBusinessInfo: (businessInfo: EmailBusinessInfo) => void;

  // Email form
  subject: string;
  setSubject: (subject: string) => void;
  body: string;
  setBody: (body: string) => void;
  attachments: EmailAttachment[];
  setAttachments: (attachments: EmailAttachment[]) => void;

  // Email Generation
  isGenerating: boolean;
  isSending: boolean;
  hasSent: boolean;
  generateEmail: (
    reason: string,
    footer: string,
    tone: string
  ) => Promise<void>;
  sendEmail: () => Promise<void>;

  // Templates
  templates: Template[];
  updateTemplate: (id: string) => Promise<Template | undefined>;
  deleteTemplate: (templateId: string) => Promise<void>;
  saveTemplate: (name: string) => Promise<Template | undefined>;
  loadTemplates: () => Promise<void>;
  getTemplate: (name: string, showError?: boolean) => Template | undefined;

  // Reset
  resetState: () => void;
}

export const useEmailStore = create<EmailState>((set, get) => {
  // Mapping from UI status to query param values
  const statusMapping: { [key: string]: string } = {
    New: "new",
    "In Progress": "in_progress",
    Waiting: "waiting",
    "Quote Sent": "quote_sent",
    "Signatures Needed": "signatures_needed",
    "Ready To E-File": "ready_to_e_file",
    Completed: "completed",
    Archived: "archived",
    "Ready To Go": "ready_to_go",
  };

  // Define a helper to refresh recipients based on current clickedStatus
  const refreshRecipients = async () => {
    const clickedStatus = useStatusStore.getState().clickedStatus;
    let params = {};
    if (clickedStatus) {
      const queryStatus = statusMapping[clickedStatus];
      if (queryStatus) {
        params = { status: queryStatus };
      }
    }
    const searchResults = await getSearchResults(params);
    const recipients: EmailIdentifier[] = searchResults.results.map((c) => ({
      name: createName(c.firstName, c.lastName),
      email: c.email,
    }));
    set({ recipients, globalRecipients: recipients });
  };

  // Subscribe to changes in clickedStatus so that we refresh the client list automatically.
  let prevClickedStatus = useStatusStore.getState().clickedStatus;
  useStatusStore.subscribe((state) => {
    if (state.clickedStatus !== prevClickedStatus) {
      prevClickedStatus = state.clickedStatus;
      if (get().source === "clientList") {
        refreshRecipients();
      }
    }
  });

  return {
    source: DEFAULT_EMAIL_SOURCE,
    setSource: async (source) => {
      set({ source });
      if (source !== "clientList") {
        return;
      }
      await attempt(
        async () => {
          // Initial load uses current clickedStatus filter.
          await refreshRecipients();
        },
        {
          errorMessage: "An error occurred while loading your client list.",
        }
      );
    },

    // Recipients management
    recipients: [],
    globalRecipients: [],
    setRecipients: (recipients) => set({ recipients }),
    setBusinessInfo: (businessInfo) => set({ businessInfo }),

    // Email content
    subject: "",
    body: "",
    attachments: [],
    setSubject: (subject) => set({ subject }),
    setBody: (body) => set({ body }),
    setAttachments: (attachments) => set({ attachments }),

    // State flags
    isGenerating: false,
    isSending: false,
    hasSent: false,

    // Generate email
    generateEmail: async (reason, footer, tone) => {
      set({ isGenerating: true });
      const response = await attempt(
        async () => {
          const { recipients } = get();
          const clientName =
            recipients.length === 1 ? recipients[0].name : "[Clients]";
          const payload = { tone, prompt: reason };
          const openAiEmail = await generateEmailOpenAi(payload);
          const emailMarkdown = `Hello ${clientName},\n\n${openAiEmail.body}\n\n${footer}`;
          set({ subject: openAiEmail.subject, body: emailMarkdown });
        },
        {
          errorMessage: "An error occurred while generating the email.",
        }
      );
      set({ isGenerating: false });
      return response;
    },

    // Send email
    sendEmail: async () => {
      set({ isSending: true });
      const { recipients, subject, body, attachments } = get();
      await attempt(
        async () => {
          if (recipients.length === 0) {
            throw new SmartwizError({
              message: "Email does not contain any recipients.",
            });
          }
          if (!subject) {
            throw new SmartwizError({
              message: "Email is missing a subject.",
            });
          }
          if (!body) {
            throw new SmartwizError({
              message: "Email is missing a body.",
            });
          }
          const response = await sendEmailData({
            to: recipients.map((recipient) => recipient.email),
            subject: subject,
            body: processEmailBody(body),
            attachments: attachments,
          });
          set({ hasSent: true });
          return response;
        },
        {
          successMessage: "Email sent!",
          errorMessage: "An error occurred while sending the email.",
        }
      );
      set({ isSending: false });
    },

    // Templates
    templates: [
      { name: NEW_TEMPLATE_NAME, id: NEW_TEMPLATE_ID, subject: "", body: "" },
    ],
    updateTemplate: async (templateId) => {
      const { subject, body, templates } = get();
      return await attempt(
        async () => {
          // Find the template to update
          const template = templates.find((t) => t.id === templateId);
          if (!template) {
            throw new SmartwizError({
              title: "Unable to update template.",
              message: "Template not found.",
            });
          }

          // Don't allow updating default templates
          if (template.isDefault) {
            throw new SmartwizError({
              title: "Unable to update template.",
              message: "Default templates cannot be updated.",
            });
          }

          // Extract numeric ID if it's a string with a prefix
          let idToUpdate = templateId;
          if (typeof templateId === "string" && templateId.startsWith("u")) {
            idToUpdate = templateId.substring(1); // Remove the 'u' prefix
          }

          const updatedTemplate = await updateEmailTemplate({
            ...template,
            id: idToUpdate, // Use the numeric ID for the API
            subject,
            body,
          });

          // Ensure the updated template has the same ID format as the original
          updatedTemplate.id = templateId;

          set({
            templates: get().templates.map((t) =>
              t.id === templateId ? updatedTemplate : t
            ),
          });
          return updatedTemplate;
        },
        {
          successMessage: "Template updated successfully!",
          errorMessage: "An error occurred while updating the template.",
        }
      );
    },
    deleteTemplate: async (templateId) => {
      return await attempt(
        async () => {
          // Don't allow deleting default templates
          const template = get().templates.find((t) => t.id === templateId);
          if (template?.isDefault) {
            throw new SmartwizError({
              title: "Unable to delete template.",
              message: "Default templates cannot be deleted.",
            });
          }

          // Extract numeric ID if it's a string with a prefix
          let idToDelete = templateId;
          if (typeof templateId === "string" && templateId.startsWith("u")) {
            idToDelete = templateId.substring(1); // Remove the 'u' prefix
          }

          await deleteEmailTemplate(idToDelete);
          set({
            templates: get().templates.filter((t) => t.id !== templateId),
          });
        },
        {
          successMessage: "Template deleted!",
          errorMessage: "An error occurred while deleting the template.",
        }
      );
    },
    saveTemplate: async (name) => {
      const { subject, body, templates, getTemplate } = get();
      return attempt<Template>(
        async () => {
          const existingTemplate = getTemplate(name, false);
          if (existingTemplate) {
            throw new SmartwizError({
              title: "Error occurred while creating template.",
              message:
                "A template with this name already exists. Please choose a different name.",
            });
          }
          const response = await createEmailTemplate({
            name: name,
            subject: subject.trim(), // Use generatedSubject directly
            body: body,
          });
          const createdTemplate = response.content.template;
          set({ templates: [...templates, createdTemplate] });
          return createdTemplate;
        },
        {
          successMessage: "Template saved successfully!",
          errorMessage: "An error occurred while saving your template.",
        }
      );
    },
    loadTemplates: async () => {
      try {
        const templates = await getEmailTemplates();

        // Ensure templates is an array
        if (!Array.isArray(templates)) {
          console.error("Templates is not an array:", templates);
          return;
        }

        const templatesWithBlank: Template[] = [
          {
            name: NEW_TEMPLATE_NAME,
            id: NEW_TEMPLATE_ID,
            subject: "",
            body: "",
          },
          ...templates,
        ];

        set({ templates: templatesWithBlank });
      } catch (error) {
        console.error("Error loading templates:", error);
        // Set at least the blank template to avoid mapping errors
        set({
          templates: [
            {
              name: NEW_TEMPLATE_NAME,
              id: NEW_TEMPLATE_ID,
              subject: "",
              body: "",
            },
          ],
        });
        notifications.show({
          message: "Failed to load email templates. Please try again later.",
          color: "red",
        });
      }
    },
    getTemplate: (name, showError = true) => {
      try {
        const { templates } = get();

        if (!templates || !Array.isArray(templates)) {
          console.error("Templates is not an array:", templates);
          return undefined;
        }

        const template = templates.find((t) => t.name === name);

        if (!template && showError) {
          console.error(`Template with name ${name} not found`);
          notifications.show({
            message: `Template "${name}" not found.`,
            color: "red",
          });
        }

        return template;
      } catch (error) {
        console.error("Error in getTemplate:", error);
        return undefined;
      }
    },

    // Reset state
    resetState: () => {
      const { source, globalRecipients } = get();
      set({
        subject: "",
        body: "",
        attachments: [],
        isGenerating: false,
        isSending: false,
        hasSent: false,
        // Initialize templates with at least the NEW_TEMPLATE option to prevent undefined errors
        templates: [
          {
            name: NEW_TEMPLATE_NAME,
            id: NEW_TEMPLATE_ID,
            subject: "",
            body: "",
          },
        ],
      });

      // If source is clientList, reset recipients to globalRecipients
      if (source === "clientList") {
        set({
          recipients: globalRecipients,
        });
      }
    },
  };
});
